// components/tour/tourDemoData.js
//
// VIEW-ONLY sample data injected while the guided tour runs (tourStore demo flag /
// the SENTINEL_JOB = -1 route). Sentinel negative IDs guarantee nothing collides
// with or writes to real records. Deleting this file + the call-site demo branches
// removes the demo layer entirely.

export const SENTINEL_JOB_ID = -1;
// A second sample job (distinct from the one seeded on the user's week) so the
// "add an entry" walkthrough shows a job already picked without reusing the block
// already on the grid. Also negative, so the same demo guards apply.
export const SENTINEL_MODAL_JOB_ID = -2;

// Staff-Suggestions page demo (route /schedule/suggestions/-1). Non-leadership
// levels so the default "hide leadership" filter keeps them visible.
export const TOUR_SUGGESTION_JOB = {
  JobId: SENTINEL_JOB_ID,
  ClientName: 'Sample Client, LLC',
  ClientCode: '12345-001',
  JobName: '2025 Audited Financial Statement',
  ServiceDesc: 'Audit',
  nicheName: 'Construction',
  Office: 'BHM',
};

export const TOUR_SUGGESTION_CANDIDATES = [
  {
    StaffId: -101, Name: 'Jordan Avery', Initials: 'JA', Level: 'Senior Accountant', DepartmentCode: 'AUDIT',
    matchTier: 'client', totalScore: 82, expScore: 88, interestScore: 70,
    clientPct: 95, nichePct: 80, servicePct: 90,
    ClientPopSize: 40, NichePopSize: 60, ServicePopSize: 120,
    ClientHours: 210, NicheHours: 540, ServiceHours: 1300,
    nicheName: 'Construction', interestRank: 1,
  },
  {
    StaffId: -102, Name: 'Riley Chen', Initials: 'RC', Level: 'Supervisor', DepartmentCode: 'AUDIT',
    matchTier: 'niche', totalScore: 64, expScore: 66, interestScore: 55,
    clientPct: 60, nichePct: 85, servicePct: 70,
    ClientPopSize: 40, NichePopSize: 60, ServicePopSize: 120,
    ClientHours: 80, NicheHours: 620, ServiceHours: 900,
    nicheName: 'Construction', interestRank: 2,
  },
  {
    StaffId: -103, Name: 'Sam Patel', Initials: 'SP', Level: 'Staff A', DepartmentCode: 'AUDIT',
    matchTier: 'none', totalScore: 38, expScore: 40, interestScore: 0,
    clientPct: 20, nichePct: 45, servicePct: 55,
    ClientPopSize: 40, NichePopSize: 60, ServicePopSize: 120,
    ClientHours: 15, NicheHours: 120, ServiceHours: 300,
    nicheName: 'Construction', interestRank: 0,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Grid + My Jobs demo (injected while tourStore.demo is on). All keyed to the
// signed-in user + sentinel ids so the sample lands on their row / My Jobs and
// nothing is ever submitted.
// ─────────────────────────────────────────────────────────────────────────────

const DEMO_CLIENT = { name: 'Sample Client, LLC', code: '12345-001' };
const DEMO_JOB_NAME = '2025 Audited Financial Statement';

function isoPlus(mondayISO, days) {
  const d = new Date(mondayISO + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Four daily schedule rows (Mon–Thu, 8h) that blocksByPerson groups into ONE
// block on the user's row. Field names match the grid's `entries` shape.
export function buildDemoEntries(myStaffId, myStaffName, mondayISO) {
  return [0, 1, 2, 3].map((i) => ({
    EntryId: -9001 - i,
    StaffId: myStaffId,
    JobId: SENTINEL_JOB_ID,
    CustomJobId: null,
    EntryDate: isoPlus(mondayISO, i),
    Hours: 8,
    EntryType: 'billable',
    ClientName: DEMO_CLIENT.name,
    ClientCode: DEMO_CLIENT.code,
    JobName: DEMO_JOB_NAME,
    JobCode: 'AUD-001',
    ServiceDesc: 'Audit',
    JobType: 'Audit',
    StaffName: myStaffName || 'You',
    JobManagerName: 'Jane Smith',
    JobBudgetHours: 120,
    ClientId: null,
    PEJobIndex: null,
    Notes: 'Sample entry shown during the tour.',
    JobStatusDetail: 'In Fieldwork',
    IsClosedInPE: 0,
    ClientStatus: null,
    CreatedBy: 'tour-demo',
  }));
}

// A (dept, level) placeholder on the grid (people + jobs view) → PlaceholderAssignModal.
export function buildDemoPlaceholder(mondayISO) {
  return {
    PlaceholderId: SENTINEL_JOB_ID,
    JobId: SENTINEL_JOB_ID,
    JobName: DEMO_JOB_NAME,
    JobCode: 'AUD-001',
    ClientName: DEMO_CLIENT.name,
    ClientCode: DEMO_CLIENT.code,
    ClientStatus: null,
    Level: 'Senior Accountant',
    DepartmentCode: 'AUDIT',
    Hours: 40,
    StartDate: isoPlus(mondayISO, 0),
    EndDate: isoPlus(mondayISO, 4),
    ServiceDesc: 'Audit',
    JobType: 'Audit',
    JobManagerName: 'Jane Smith',
  };
}

// The Jobs-view job sidebar `_job` object (setSelectedBlock({_viewMode:'jobs', _job})).
export const TOUR_JOB_SIDEBAR_OBJ = {
  jobId: SENTINEL_JOB_ID,
  jobName: DEMO_JOB_NAME,
  jobCode: 'AUD-001',
  clientName: DEMO_CLIENT.name,
  clientCode: DEMO_CLIENT.code,
  clientId: null,
  jobType: 'Audit',
  serviceDesc: 'Audit',
  budgetHours: 120,
  managerName: 'Jane Smith',
  managerId: null,
  partnerName: 'John Partner',
  partnerId: null,
  nicheName: 'Construction',
  clientIndustry: 'Construction',
  isOnSite: false,
  office: 'Birmingham',
  officeCode: 'BHM',
  isClosedInPE: false,
  clientStatus: null,
};

// allJobs-shaped rows for the grid's job picker (the modal's selected-job card +
// jobInfoMap + search). Injected into `allJobs` only while the tour runs so the
// sentinel jobs resolve to real names instead of showing blank.
//   -1 = the Audit job already seeded on the user's week (used by grid-modal-suggest)
//   -2 = a distinct Tax job pre-picked in the "add an entry" walkthrough
export const TOUR_GRID_JOBS = [
  {
    JobId: SENTINEL_JOB_ID, ClientId: SENTINEL_JOB_ID, Code: 'AUD-001',
    ClientName: DEMO_CLIENT.name, ClientCode: DEMO_CLIENT.code, Name: DEMO_JOB_NAME,
    ServiceDesc: 'Audit', JobType: 'AUDIT', DepartmentCode: 'AUDIT', BudgetHours: 120,
    ManagerId: null, PartnerId: null, IsOnSite: 0, OfficeCode: 'BHM', Office: 'Birmingham',
    NicheName: 'Construction', ClientIndustry: 'Construction', ClientStatus: null, PEJobIndex: null,
  },
  {
    JobId: SENTINEL_MODAL_JOB_ID, ClientId: SENTINEL_MODAL_JOB_ID, Code: 'TAX-114',
    ClientName: 'Riverside Builders, Inc.', ClientCode: '67890-002', Name: '2025 Form 1120 Corporate Tax Return',
    ServiceDesc: 'Tax', JobType: 'TAX', DepartmentCode: 'TAX', BudgetHours: 45,
    ManagerId: null, PartnerId: null, IsOnSite: 0, OfficeCode: 'BHM', Office: 'Birmingham',
    NicheName: 'Construction', ClientIndustry: 'Construction', ClientStatus: null, PEJobIndex: null,
  },
];

// My Jobs demo: a raw job row (sentinel) owned by the signed-in user, plus a few
// today-onward schedule rows so it shows a partial progress bar.
export function buildDemoMyJob(myStaffId) {
  return {
    JobId: SENTINEL_JOB_ID,
    ClientId: SENTINEL_JOB_ID,
    Code: 'AUD-001',
    Name: DEMO_JOB_NAME,
    JobType: 'AUDIT',
    JobClass: 'A',
    JobClassDesc: 'Audit',
    Status: 'In Progress',
    StatusDetail: 'In Fieldwork',
    ServiceDesc: 'Audit',
    ServiceIndex: 1,
    PeriodStart: '2025-01-01',
    PeriodEnd: '2025-12-31',
    StartDate: '2026-01-15',
    DueDate: '2026-09-15',
    Deadline: '2026-09-15',
    BudgetHours: 120,
    BudgetValue: 0, BudgetPartner: 30, BudgetManager: 30, BudgetSenior: 30, BudgetStaff: 30,
    PartnerId: myStaffId,
    ManagerId: myStaffId,
    InChargeId: null,
    Office: 'Birmingham', OfficeCode: 'BHM',
    IsOnSite: 0,
    DepartmentCode: 'AUDIT',
    PEJobIndex_PY: 99001,   // enables the Roll Forward button
    PEJobIndex: 99002,
    ClientName: DEMO_CLIENT.name,
    ClientCode: DEMO_CLIENT.code,
    ClientGrade: 'A',
    ClientStatus: null,
    ClientIndustry: 'Construction',
    IndustryCode: '3000',
    PartnerName: 'You',
    ManagerName: 'You',
    InChargeName: null,
    NicheName: 'Construction',
  };
}

export function buildDemoMyJobSchedule(myStaffId, mondayISO) {
  return [0, 1, 2].map((i) => ({
    EntryId: -9101 - i,
    StaffId: myStaffId,
    JobId: SENTINEL_JOB_ID,
    EntryDate: isoPlus(mondayISO, i),
    Hours: 8,
  }));
}

// Roll Forward preview payload (bypasses the API). Mix: 2 active resolved, 1
// departed→placeholder (valid level, no picker), 1 unresolved (junk level → picker).
export const TOUR_RF_PREVIEW = {
  preview: true,
  pyJob: {
    Name: `${DEMO_CLIENT.name} — 2024 Audited Financial Statement`,
    BudgetHours: 110,
    ActualHours: 96.5,
    PEJobIndex: 99001,
    ServiceDesc: 'Audit',
  },
  team: [
    { staffId: -201, peStaffIndex: 5001, name: 'Jane Smith', initials: 'JS', level: 'Manager', department: 'AUDIT', pyHours: 34, firstDate: '2024-01-08', lastDate: '2024-02-16', weekCount: 6, isActive: true, roles: ['Engagement Leader'] },
    { staffId: -202, peStaffIndex: 5002, name: 'Carlos Rivera', initials: 'CR', level: 'Senior Accountant', department: 'AUDIT', pyHours: 28, firstDate: '2024-01-08', lastDate: '2024-02-09', weekCount: 5, isActive: true, roles: ['In-Charge'] },
    { staffId: -203, peStaffIndex: 5003, name: 'Alex Johnson', initials: 'AJ', level: 'Staff A', department: 'AUDIT', pyHours: 22.5, firstDate: '2024-01-15', lastDate: '2024-02-02', weekCount: 3, isActive: false, roles: ['Preparer'] },
    { staffId: null, peStaffIndex: 5099, name: 'Staff #5099', initials: '??', level: 'No Selection', department: '', pyHours: 8, firstDate: '2024-01-22', lastDate: '2024-01-26', weekCount: 1, isActive: true, roles: [] },
  ],
};

// Pre-computed rfSelected (mirrors computeRfSelections) so the drive handler can
// seed it without calling the component's useCallback. Keyed by staffId||peStaffIndex.
export const TOUR_RF_SELECTED = {
  '-201': { checked: true, hours: 34, weeks: 6, offsetWeeks: 0, phLevel: '', roles: ['Engagement Leader'] },
  '-202': { checked: true, hours: 28, weeks: 5, offsetWeeks: 0, phLevel: '', roles: ['In-Charge'] },
  '-203': { checked: false, hours: 22.5, weeks: 3, offsetWeeks: 1, phLevel: '', roles: ['Preparer'] },
  '5099': { checked: false, hours: 8, weeks: 1, offsetWeeks: 2, phLevel: '', roles: [] },
};
